import React, { useState, useEffect, useRef } from "react";
import { Button, Typography, Box, Chip, IconButton, TextField } from "@mui/material";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import ArrowLeftIcon from "@mui/icons-material/ArrowLeft";
import PersonIcon from "@mui/icons-material/Person";
import SelectAllIcon from "@mui/icons-material/SelectAll";
import RuleIcon from "@mui/icons-material/Rule";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SelectionArea from "@viselect/react";
import "./style.css";
import useStyles from "./KaraokeStyles";
import LyicsSelectionInstructions from "./Components/LyicsSelectionInstructions";

const PLAYER_KEYS = ["playerOne", "playerTwo", "playerThree", "playerFour"];
/** Group “Alternate” order: Person 1 → 4 → 2 → 3 */
const GROUP_ALTERNATE_KEYS = [
  "playerOne",
  "playerFour",
  "playerTwo",
  "playerThree",
];

const PLAYER_COLORS = {
  playerOne: "#3f51b5",
  playerTwo: "#4caf50",
  playerThree: "#9c27b0",
  playerFour: "#009688",
  bothPlayers: "#de7e09",
};

const getLineColor = (index, assignedLyrics) => {
  for (const key of PLAYER_KEYS) {
    if (assignedLyrics[key]?.includes(index)) return PLAYER_COLORS[key];
  }
  if (assignedLyrics.bothPlayers.includes(index)) return PLAYER_COLORS.bothPlayers;
  return "textSecondary";
};

const LyricsSelection = ({
  showAssignLyrics,
  enablePreview,
  handleShowPreview,
  goBackToSyncLyrics,
  goBackToDetails,
  onContinueToGroup,
  onBackToSolo,
  onEditLine,
  onDeleteLine,
  sectionTitle,
  lines,
  assignedLyrics,
  setAssignedLyrics,
  setShowAssignLyrics,
  setEnablePreview,
  currentLine,
  isGroupMode = false,
}) => {
  const [selected, setSelected] = useState(() => new Set());
  const [editingLine, setEditingLine] = useState(null);
  const [editingText, setEditingText] = useState("");
  const classes = useStyles();
  const dragOccurredRef = useRef(false);
  const lastClickedRef = useRef(null);
  const skipCommitRef = useRef(false);

  const activePlayerKeys = isGroupMode
    ? GROUP_ALTERNATE_KEYS
    : PLAYER_KEYS.slice(0, 2);

  const extractIds = (els) =>
    els
      .map((v) => v.getAttribute("data-key"))
      .filter(Boolean)
      .map(Number);

  // Don't let drag-selection start (and wipe the current selection) when the
  // admin is clicking a per-line edit/delete button or typing in the editor.
  const onBeforeStart = ({ event }) => {
    const target = event?.target;
    if (
      target?.closest?.(".line-action-btn") ||
      target?.closest?.(".line-edit-field")
    ) {
      return false;
    }
    return true;
  };

  const onStart = ({ event, selection }) => {
    dragOccurredRef.current = false;
    if (!event?.ctrlKey && !event?.metaKey && !event?.shiftKey) {
      selection.clearSelection();
      setSelected(() => new Set());
    }
  };

  const onMove = ({
    store: {
      changed: { added, removed },
    },
  }) => {
    if (added.length || removed.length) {
      dragOccurredRef.current = true;
    }
    setSelected((prev) => {
      const next = new Set(prev);
      extractIds(added).forEach((id) => next.add(id));
      extractIds(removed).forEach((id) => next.delete(id));
      return next;
    });
  };

  const assignLyricsToPlayer = (player) => {
    const selectedLyrics = Array.from(selected);
    const updatedLyrics = { ...assignedLyrics };

    PLAYER_KEYS.forEach((key) => {
      if (player === key) {
        updatedLyrics[key] = [...new Set([...(updatedLyrics[key] || []), ...selectedLyrics])];
      } else {
        updatedLyrics[key] = (updatedLyrics[key] || []).filter(
          (val) => !selectedLyrics.includes(val)
        );
      }
    });

    if (player === "bothPlayers") {
      PLAYER_KEYS.forEach((key) => {
        updatedLyrics[key] = (updatedLyrics[key] || []).filter(
          (val) => !selectedLyrics.includes(val)
        );
      });
      updatedLyrics.bothPlayers = [
        ...new Set([...(updatedLyrics.bothPlayers || []), ...selectedLyrics]),
      ];
    } else {
      updatedLyrics.bothPlayers = (updatedLyrics.bothPlayers || []).filter(
        (val) => !selectedLyrics.includes(val)
      );
    }

    PLAYER_KEYS.forEach((key) => {
      updatedLyrics[key]?.sort((a, b) => a - b);
    });
    updatedLyrics.bothPlayers.sort((a, b) => a - b);

    setAssignedLyrics(updatedLyrics);
    setSelected(() => new Set());
  };

  const selectableIndexes = lines
    .map((line, index) => (line.trim().length > 0 ? index : null))
    .filter((value) => value !== null);

  const isAssigned = (index) =>
    [...PLAYER_KEYS, "bothPlayers"].some((key) =>
      assignedLyrics[key]?.includes(index)
    );

  const unassignedIndexes = selectableIndexes.filter(
    (index) => !isAssigned(index)
  );

  const selectAllLines = () => {
    setSelected(() => new Set(selectableIndexes));
  };

  const selectUnassignedLines = () => {
    setSelected(() => new Set(unassignedIndexes));
  };

  // Distribute the selected lines round-robin across the active persons
  // (Person 1–2 in solo mode, Person 1–4 in group mode), in line order.
  const alternateAssignSelection = () => {
    const orderedSelection = Array.from(selected).sort((a, b) => a - b);
    if (orderedSelection.length === 0) return;

    const selectedSet = new Set(orderedSelection);
    const updatedLyrics = { ...assignedLyrics };

    PLAYER_KEYS.forEach((key) => {
      updatedLyrics[key] = (updatedLyrics[key] || []).filter(
        (val) => !selectedSet.has(val)
      );
    });
    updatedLyrics.bothPlayers = (updatedLyrics.bothPlayers || []).filter(
      (val) => !selectedSet.has(val)
    );

    orderedSelection.forEach((lineIndex, i) => {
      const key = activePlayerKeys[i % activePlayerKeys.length];
      updatedLyrics[key] = [...updatedLyrics[key], lineIndex];
    });

    PLAYER_KEYS.forEach((key) => {
      updatedLyrics[key]?.sort((a, b) => a - b);
    });

    setAssignedLyrics(updatedLyrics);
    setSelected(() => new Set());
  };

  useEffect(() => {
    if (selected.size === 0) {
      setShowAssignLyrics(false);
    }

    if (selected.size > 0) {
      setShowAssignLyrics(true);
    }

    const totalSelectedLyrics = [
      ...(assignedLyrics.playerOne || []),
      ...(assignedLyrics.playerTwo || []),
      ...(assignedLyrics.playerThree || []),
      ...(assignedLyrics.playerFour || []),
      ...(assignedLyrics.bothPlayers || []),
    ].length;
    const totalLyrics = lines.filter((line) => line.trim().length > 0).length;
    setEnablePreview(totalSelectedLyrics === totalLyrics);

    const scrollTargets = [
      ...PLAYER_KEYS.map((key) => document.querySelector(`.line-${key}-${currentLine}`)),
      document.querySelector(`.line-both-players-${currentLine}`),
    ];

    scrollTargets.forEach((line) => {
      if (line) {
        line.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    });
  }, [selected, assignedLyrics, lines, currentLine, setEnablePreview, setShowAssignLyrics]);

  const getClassName = (line, index) => {
    if (line.trim().length === 0) return "";
    if (editingLine === index) return "";
    return selected.has(index) ? "selected selectable" : "selectable";
  };

  const startLineEdit = (index) => {
    skipCommitRef.current = false;
    lastClickedRef.current = index;
    setEditingLine(index);
    setEditingText(lines[index]);
  };

  const commitLineEdit = () => {
    if (editingLine === null) return;
    if (skipCommitRef.current) {
      skipCommitRef.current = false;
      return;
    }
    const index = editingLine;
    const nextText = editingText;
    setEditingLine(null);
    setEditingText("");
    // Emptied lines are reverted — use the delete button to remove a line.
    if (!nextText.trim() || nextText === lines[index]) return;
    onEditLine?.(index, nextText);
  };

  const cancelLineEdit = () => {
    skipCommitRef.current = true;
    setEditingLine(null);
    setEditingText("");
  };

  const deleteLine = (index, line) => {
    const confirmed = window.confirm(
      `Delete this lyric line?\n\n"${line.trim()}"\n\nIts timing and solo/group assignments will be removed, and later lines will shift up.`
    );
    if (!confirmed) return;
    onDeleteLine?.(index);
    // Remap this screen's own selection to the shifted indexes.
    setSelected((prev) => {
      const next = new Set();
      prev.forEach((value) => {
        if (value === index) return;
        next.add(value > index ? value - 1 : value);
      });
      return next;
    });
    if (lastClickedRef.current !== null) {
      if (lastClickedRef.current === index) {
        lastClickedRef.current = null;
      } else if (lastClickedRef.current > index) {
        lastClickedRef.current -= 1;
      }
    }
  };

  const toggleLineSelection = (event, line, index) => {
    if (line.trim().length === 0) return;
    if (editingLine === index) return;
    if (dragOccurredRef.current) {
      dragOccurredRef.current = false;
      return;
    }
    // Shift+click selects the whole range between the last clicked line and this one
    if (event?.shiftKey && lastClickedRef.current !== null) {
      const start = Math.min(lastClickedRef.current, index);
      const end = Math.max(lastClickedRef.current, index);
      setSelected((prev) => {
        const next = new Set(prev);
        for (let i = start; i <= end; i++) {
          if (lines[i]?.trim().length > 0) next.add(i);
        }
        return next;
      });
      lastClickedRef.current = index;
      return;
    }
    lastClickedRef.current = index;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const clearSelection = () => {
    setSelected(() => new Set());
  };

  const assignButtons = isGroupMode
    ? [
        { player: "playerOne", label: "Person 1", color: "primary" },
        { player: "playerTwo", label: "Person 2", color: "success" },
        { player: "playerThree", label: "Person 3", sx: { backgroundColor: "#9c27b0 !important" } },
        { player: "playerFour", label: "Person 4", sx: { backgroundColor: "#009688 !important" } },
        { player: "bothPlayers", label: "All", color: "custom", textColor: "white" },
      ]
    : [
        { player: "playerOne", label: "Person 1", color: "primary" },
        { player: "playerTwo", label: "Person 2", color: "success" },
        { player: "bothPlayers", label: "Both players", color: "custom", textColor: "white" },
      ];

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      height="90vh"
    >
      <LyicsSelectionInstructions isGroupMode={isGroupMode} />
      {sectionTitle && (
        <Typography className={classes.commandsTitle} sx={{ mb: 1 }}>
          {sectionTitle}
        </Typography>
      )}
      <Box flexGrow={1} width="100%" overflow="auto" className="selectionArea">
        <SelectionArea
          onBeforeStart={onBeforeStart}
          onStart={onStart}
          onMove={onMove}
          selectables=".selectable"
        >
          {lines.map((line, index) => (
            <Box
              key={`${line}-${index}`}
              display="flex"
              alignItems="center"
              justifyContent={"center"}
              mb={1}
              className={getClassName(line, index)}
              data-key={index}
              onClick={(event) => toggleLineSelection(event, line, index)}
              sx={{
                cursor: line.trim().length === 0 ? "default" : "pointer",
                userSelect: "none",
                py: { xs: 0.75, sm: 0.25 },
                px: { xs: 1, sm: 0.5 },
                minHeight: { xs: "36px", sm: "auto" },
                borderRadius: "4px",
                "&:hover .line-action-btn": { opacity: 1 },
              }}
            >
              {editingLine === index ? (
                <TextField
                  className="line-edit-field"
                  value={editingText}
                  onChange={(e) => setEditingText(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      commitLineEdit();
                    } else if (e.key === "Escape") {
                      cancelLineEdit();
                    }
                  }}
                  onBlur={commitLineEdit}
                  variant="outlined"
                  size="small"
                  autoFocus
                  inputProps={{ style: { textAlign: "center", fontSize: "16px" } }}
                  sx={{
                    width: `${Math.max(editingText.length + 4, 16)}ch`,
                    maxWidth: "90%",
                  }}
                />
              ) : (
                <>
                  <Typography
                    variant="body1"
                    color={getLineColor(index, assignedLyrics)}
                    sx={{ fontSize: { xs: "15px", sm: "16px" } }}
                    onDoubleClick={(e) => {
                      if (line.trim().length === 0) return;
                      e.stopPropagation();
                      startLineEdit(index);
                    }}
                  >
                    {line}
                  </Typography>
                  {line.trim().length > 0 && (
                    <Box
                      component="span"
                      sx={{ display: "inline-flex", ml: 1, gap: 0.25 }}
                    >
                      <IconButton
                        className="line-action-btn"
                        size="small"
                        title="Edit lyric text"
                        onClick={(e) => {
                          e.stopPropagation();
                          startLineEdit(index);
                        }}
                        sx={{
                          opacity: { xs: 0.5, md: 0.25 },
                          p: 0.25,
                          transition: "opacity 0.15s",
                        }}
                      >
                        <EditIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                      <IconButton
                        className="line-action-btn"
                        size="small"
                        color="error"
                        title="Delete lyric line"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteLine(index, line);
                        }}
                        sx={{
                          opacity: { xs: 0.5, md: 0.25 },
                          p: 0.25,
                          transition: "opacity 0.15s",
                        }}
                      >
                        <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                  )}
                </>
              )}
            </Box>
          ))}
        </SelectionArea>
      </Box>

      <Box
        mt={2}
        display="flex"
        alignItems="center"
        gap={{ xs: 1, sm: 1.5 }}
        justifyContent={{ xs: "flex-start", sm: "center" }}
        flexWrap={{ xs: "nowrap", md: "wrap" }}
        sx={{
          width: "100%",
          overflowX: { xs: "auto", md: "visible" },
          pb: { xs: 1, md: 0 },
          "&::-webkit-scrollbar": { height: "6px" },
        }}
      >
        <Button
          size="small"
          variant="outlined"
          startIcon={<SelectAllIcon />}
          onClick={selectAllLines}
          disabled={selectableIndexes.length === 0}
          sx={{ flexShrink: 0, textTransform: "none" }}
        >
          Select All
        </Button>
        <Button
          size="small"
          variant="outlined"
          startIcon={<RuleIcon />}
          onClick={selectUnassignedLines}
          disabled={unassignedIndexes.length === 0}
          sx={{ flexShrink: 0, textTransform: "none" }}
        >
          Select Unassigned ({unassignedIndexes.length})
        </Button>
        <Button
          size="small"
          variant="outlined"
          startIcon={<SwapVertIcon />}
          onClick={alternateAssignSelection}
          disabled={!showAssignLyrics}
          sx={{ flexShrink: 0, textTransform: "none" }}
        >
          Alternate 1–{activePlayerKeys.length}
        </Button>
        {selected.size > 0 && (
          <Chip
            size="small"
            label={`${selected.size} line${selected.size === 1 ? "" : "s"} selected`}
            sx={{ flexShrink: 0 }}
          />
        )}
      </Box>

      <Box
        mt={1.5}
        display="flex"
        gap={{ xs: 1, sm: 2 }}
        justifyContent={{ xs: "flex-start", sm: "center" }}
        flexWrap={{ xs: "nowrap", md: "wrap" }}
        sx={{
          width: "100%",
          overflowX: { xs: "auto", md: "visible" },
          pb: { xs: 1, md: 0 },
          "&::-webkit-scrollbar": { height: "6px" },
        }}
      >
        {assignButtons.map(({ player, label, color, sx, textColor }) => (
          <Button
            key={player}
            sx={{ ...classes.buttonStyle, color: textColor, flexShrink: 0, ...sx }}
            variant="contained"
            color={color}
            disabled={!showAssignLyrics}
            onClick={() => assignLyricsToPlayer(player)}
            startIcon={<PersonIcon />}
          >
            {label}
          </Button>
        ))}
        <Button
          sx={{ ...classes.buttonStyle, flexShrink: 0 }}
          variant="outlined"
          disabled={!showAssignLyrics}
          onClick={clearSelection}
        >
          Clear
        </Button>
        <Button
          sx={{ ...classes.buttonStyle, flexShrink: 0 }}
          variant="contained"
          disabled={!enablePreview}
          onClick={isGroupMode ? handleShowPreview : onContinueToGroup}
          endIcon={<ArrowRightIcon />}
        >
          {isGroupMode ? "Preview" : "Continue to Group"}
        </Button>

        {isGroupMode ? (
          <Button
            sx={{ ...classes.buttonStyle, flexShrink: 0 }}
            variant="contained"
            onClick={onBackToSolo || goBackToSyncLyrics}
            startIcon={<ArrowLeftIcon />}
          >
            Back to Solo
          </Button>
        ) : (
          <>
            <Button
              sx={{ ...classes.buttonStyle, flexShrink: 0 }}
              variant="contained"
              onClick={goBackToSyncLyrics}
              startIcon={<ArrowLeftIcon />}
            >
              Back to Sync
            </Button>
            {goBackToDetails && (
              <Button
                sx={{ ...classes.buttonStyle, flexShrink: 0 }}
                variant="contained"
                onClick={goBackToDetails}
                startIcon={<ArrowLeftIcon />}
              >
                Back to Details
              </Button>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default LyricsSelection;
